import { mapSchemaLeaves } from '../mapSchemaLeaves';
import type { HireFormField, HireFormSchema } from './types';

/**
 * Carry `required: true` but belong to `create_position`, which this form does
 * not call — and `create_hire` does not accept them. Left required they would
 * block a submit that never needed them.
 */
const POSITION_ONLY_FIELDS =
  /^(job[\s_-]?posting[\s_-]?title|worker[\s_-]?type)/i;

/**
 * Folds Workday's three extra keys into the shape the existing renderer already
 * understands, so `convertSchemaToFormConfig` needs no changes:
 *
 *  - `value`    the tenant's configured default → `default_value`, which the
 *               form already lifts into initial values and seeds into a
 *               select's option list. The field stays editable.
 *  - `editable` `false` → `disabled`. The position decided these eight
 *               fields, and they stay greyed out: a `restricted: true`
 *               variant (filled but changeable) was planned alongside a
 *               position picker, and both were cancelled backend-side.
 *  - `source`   left alone; it is provenance for a "from offer" hint later.
 *
 * A `default_value` Kombo already supplied wins: that one came from the HRIS
 * itself and is better evidence than the tenant default.
 */
export const applyWorkdayHireDefaults = (
  schema: HireFormSchema | undefined
): HireFormSchema | undefined => {
  return mapSchemaLeaves(schema, (key, field) => {
    const leaf = field as HireFormField;
    const patch: Partial<HireFormField> = {};

    if (leaf.required && POSITION_ONLY_FIELDS.test(key)) patch.required = false;

    // A `default_value` Kombo already supplied wins: that one came from the
    // HRIS itself and is better evidence than the tenant default.
    const hasOwnDefault =
      leaf.default_value !== undefined && leaf.default_value !== null;
    if (!hasOwnDefault && leaf.value !== undefined && leaf.value !== null) {
      patch.default_value = leaf.value;
    }

    // The position decided this field; Workday validates the combination, so
    // the recruiter cannot usefully change it here.
    if (leaf.editable === false) patch.disabled = true;

    return patch;
  }) as HireFormSchema | undefined;
};
